/**
 * Flythrough videos for the stand demo.
 *
 * The flythrough is a fixed path: the camera pose at any moment t is known
 * (CameraTour.applyAt). So the device itself can record it — step t by
 * 1/60 s, wait at each step until the view has every tier applied and the
 * sorter caught up, take that frame, encode it. Slow to make, but every
 * frame is full detail on the device's own renderer, and played back as a
 * video it runs at the screen's rate whatever the scene costs to draw.
 *
 *   record   Setup → "Record flythroughs" mounts each scene with
 *            `record`, mount() calls recordFlythrough() once the loader is
 *            gone, the file lands in the ollo-demo-flythrough cache
 *   play     mount() looks the video up for this scene and orientation;
 *            CameraTour plays it instead of flying live and fades back to
 *            the live scene at the last pose
 *
 * A video is keyed by scene URL and orientation only, never the exact
 * window size: it is shown cropped to fill whatever window it plays in
 * (`object-fit: cover`). The camera's fov is on the larger axis (mount.ts,
 * `horizontalFov`), so in landscape a window wider than the recording sees
 * the same picture as the live camera, its top and bottom cut — the
 * hand-back lines up. A window narrower than the recording gets a slight
 * zoom at the hand-back instead of a squish. Setup records the Mac
 * full screen for that reason: every smaller window is then wider.
 *
 * Its stamp is the scene.json text plus RECORD_VERSION, so a re-published
 * scene (path, lighting, splats) or a renderer change makes it stale, and a
 * stale video is never played — the ▶ falls back to the live flythrough.
 * Nothing else invalidates a video: a viewer fix that leaves the picture
 * alone must not bump RECORD_VERSION.
 */

import type * as pc from 'playcanvas';

import { colourFrom, writeMp4, type Mp4Colour } from './mp4-writer';

export const FLY_CACHE = 'ollo-demo-flythrough';

/** Bump when anything that changes the recorded picture changes in the
 *  viewer (renderer, tonemap, quality rung 0, the tour's easing). Every
 *  stored video then reads "needs recording". */
export const RECORD_VERSION = 1;

export const RECORD_FPS = 60;

// ── Keys and stamps ───────────────────────────────────────────────────────

export interface ScreenSize { w: number; h: number; dpr: number }

export function screenSize(): ScreenSize {
    return { w: window.innerWidth, h: window.innerHeight, dpr: window.devicePixelRatio || 1 };
}

const sizeTag = (s: ScreenSize) => `${s.w}x${s.h}@${s.dpr}`;

export type Orientation = 'landscape' | 'portrait';

/** Same rule as the camera's fov axis in mount.ts. */
export function orientation(): Orientation {
    return window.innerWidth >= window.innerHeight ? 'landscape' : 'portrait';
}

const scenePrefix = (sceneUrl: string) => `https://flythrough.invalid/${encodeURIComponent(sceneUrl)}/`;

function keyFor(sceneUrl: string, o: Orientation): string {
    return `${scenePrefix(sceneUrl)}${o}.mp4`;
}

/** FNV-1a over the scene.json text, as 8 hex digits. */
function hash(text: string): string {
    let h = 0x811c9dc5;
    for (let i = 0; i < text.length; i++) {
        h ^= text.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return (h >>> 0).toString(16).padStart(8, '0');
}

/** What a video must have been recorded from to be played: this scene.json
 *  (served from the offline cache when there is one) and this viewer. */
export async function sceneStamp(sceneUrl: string): Promise<string | null> {
    try {
        const res = await fetch(sceneUrl);
        if (!res.ok) return null;
        return `${RECORD_VERSION}:${hash(await res.text())}`;
    } catch {
        return null;
    }
}

// ── Store ─────────────────────────────────────────────────────────────────

export type FlyState =
    | { state: 'ready'; bytes: number; at: number; orientation: Orientation }
    | { state: 'stale'; orientation: Orientation }
    | { state: 'other-orientation'; orientation: Orientation }
    | { state: 'none'; orientation: Orientation };

export async function flythroughState(sceneUrl: string): Promise<FlyState> {
    const o = orientation();
    const stamp = await sceneStamp(sceneUrl);
    const cache = await caches.open(FLY_CACHE);
    const res = await cache.match(keyFor(sceneUrl, o));
    if (res) {
        if (!stamp || res.headers.get('x-ollo-stamp') !== stamp) return { state: 'stale', orientation: o };
        return {
            state: 'ready',
            orientation: o,
            bytes: Number(res.headers.get('content-length')) || 0,
            at: Number(res.headers.get('x-ollo-at')) || 0
        };
    }
    const other = await cache.match(keyFor(sceneUrl, o === 'landscape' ? 'portrait' : 'landscape'));
    return other ? { state: 'other-orientation', orientation: o } : { state: 'none', orientation: o };
}

/** A playable URL for this scene in this orientation, or null (none, or
 *  stale). The caller revokes it. */
export async function flythroughUrl(sceneUrl: string): Promise<{ url: string; key: Orientation } | null> {
    const o = orientation();
    try {
        const cache = await caches.open(FLY_CACHE);
        const res = await cache.match(keyFor(sceneUrl, o));
        if (!res) {
            console.info(`[tour] video lookup: none stored for ${o}`);
            return null;
        }
        const stamp = await sceneStamp(sceneUrl);
        if (!stamp || res.headers.get('x-ollo-stamp') !== stamp) {
            console.info(`[tour] video lookup: stamp differs (stored ${res.headers.get('x-ollo-stamp')}, scene now ${stamp})`);
            return null;
        }
        const blob = await res.blob();
        console.info(`[tour] video lookup: found, ${(blob.size / 1e6).toFixed(1)} MB, ${o}, recorded at ${res.headers.get('x-ollo-size') ?? '?'}, window now ${sizeTag(screenSize())}`);
        return { url: URL.createObjectURL(blob), key: o };
    } catch (e) {
        console.warn('[tour] video lookup failed', e);
        return null;
    }
}

async function storeFlythrough(sceneUrl: string, o: Orientation, s: ScreenSize, stamp: string, blob: Blob): Promise<void> {
    const cache = await caches.open(FLY_CACHE);
    await cache.put(keyFor(sceneUrl, o), new Response(blob, {
        headers: {
            'content-type': 'video/mp4',
            'content-length': String(blob.size),
            'x-ollo-stamp': stamp,
            'x-ollo-size': sizeTag(s),
            'x-ollo-at': String(Date.now())
        }
    }));
    // Videos stored under the old per-window-size keys (before 19/10/2026)
    // are never looked up again; free their space.
    const keep = new Set([keyFor(sceneUrl, 'landscape'), keyFor(sceneUrl, 'portrait')]);
    for (const req of await cache.keys()) {
        if (req.url.startsWith(scenePrefix(sceneUrl)) && !keep.has(req.url)) await cache.delete(req);
    }
}

export async function clearFlythroughs(): Promise<void> {
    await caches.delete(FLY_CACHE);
}

// ── Settling ──────────────────────────────────────────────────────────────

/**
 * Resolve once the current view is drawn at full detail: the unified gsplat
 * pipeline reports `ready` (sorter caught up) with an unchanged pending
 * count for `stableEvents` frames in a row, after at least `minFrames`.
 * Same signal as mount's waitForSplatSettled, per view instead of per load.
 * `capMs` bounds a view that never settles; plain .ply scenes (no event)
 * settle after `minFrames`.
 */
export function waitViewSettled(
    app: pc.AppBase,
    opts: { minFrames?: number; stableEvents?: number; capMs?: number } = {}
): Promise<'stable' | 'cap' | 'no-event'> {
    const minFrames = opts.minFrames ?? 2;
    const need = opts.stableEvents ?? 3;
    const capMs = opts.capMs ?? 4000;
    return new Promise((resolve) => {
        const sys = (app.systems as unknown as { gsplat?: pc.EventHandler }).gsplat;
        let frames = 0;
        let sawEvent = false;
        let last = -1;
        let stable = 0;
        const start = performance.now();
        const finish = (r: 'stable' | 'cap' | 'no-event') => {
            sys?.off('frame:ready', onReady);
            app.off('frameend', onFrame);
            resolve(r);
        };
        const onReady = (_c: unknown, _l: unknown, ready: boolean, loading: number) => {
            sawEvent = true;
            if (ready && loading === last) stable++;
            else stable = 0;
            last = loading;
        };
        const onFrame = () => {
            frames++;
            app.renderNextFrame = true;
            if (frames < minFrames) return;
            if (sawEvent && stable >= need) finish('stable');
            else if (!sawEvent && frames >= minFrames + 6) finish('no-event');
            else if (performance.now() - start > capMs) finish('cap');
        };
        sys?.on('frame:ready', onReady);
        app.on('frameend', onFrame);
        app.renderNextFrame = true;
    });
}

// ── Recording ─────────────────────────────────────────────────────────────

export interface RecordTarget {
    app: pc.AppBase;
    canvas: HTMLCanvasElement;
    sceneUrl: string;
    /** Tour length in seconds. */
    duration: number;
    /** Put the camera where the tour is at `t` seconds. */
    applyAt: (t: number) => void;
}

export interface RecordProgress {
    frame: number;
    frames: number;
    /** Views that hit the per-frame cap instead of settling. */
    capped: number;
    elapsedMs: number;
}

const CODECS = ['avc1.640034', 'avc1.640033', 'avc1.4d0034', 'avc1.4d0033', 'avc1.42003e'];

type Accel = 'no-preference' | 'prefer-software';

async function pickConfig(w: number, h: number, accel: Accel): Promise<VideoEncoderConfig> {
    // ~0.1 bit per pixel per frame: 2360×1640 at 60 fps ≈ 23 Mbit/s,
    // about 90 MB for a 30 s flythrough. Smooth gradients in splat scenes
    // band below that.
    const bitrate = Math.round(w * h * RECORD_FPS * 0.1);
    for (const codec of CODECS) {
        const cfg: VideoEncoderConfig = {
            codec, width: w, height: h, bitrate, framerate: RECORD_FPS,
            bitrateMode: 'variable', latencyMode: 'quality',
            hardwareAcceleration: accel,
            avc: { format: 'avc' }
        } as VideoEncoderConfig;
        try {
            const r = await VideoEncoder.isConfigSupported(cfg);
            if (r.supported) return r.config ?? cfg;
        } catch { /* next */ }
    }
    throw new Error(`no H.264 encoder for ${w}×${h} on this device`);
}

let scratch: HTMLCanvasElement | null = null;
let scratchCtx: CanvasRenderingContext2D | null = null;

/** Take the frame drawn in this tick. Called from 'frameend', inside the
 *  same task as the render, while the canvas still holds it. */
function grab(app: pc.AppBase, canvas: HTMLCanvasElement, w: number, h: number, timestamp: number): Promise<VideoFrame> {
    return new Promise((resolve, reject) => {
        const onEnd = () => {
            app.off('frameend', onEnd);
            try {
                // A VideoFrame made straight from a WebGPU canvas only points
                // at that frame's texture, which is gone by the time the
                // encoder reads it ("Can't readback frame textures", MacBook
                // Chrome, 19/10/2026). Copy the pixels into a plain CPU canvas
                // here, in the render's own task, and frame that.
                if (!scratch || scratch.width !== w || scratch.height !== h) {
                    scratch = document.createElement('canvas');
                    scratch.width = w;
                    scratch.height = h;
                    scratchCtx = scratch.getContext('2d', { willReadFrequently: true });
                }
                if (!scratchCtx) throw new Error('no 2D canvas to copy the frame into');
                scratchCtx.drawImage(canvas, 0, 0, w, h, 0, 0, w, h);
                resolve(new VideoFrame(scratch, {
                    timestamp,
                    duration: Math.round(1e6 / RECORD_FPS)
                }));
            } catch (e) {
                reject(e);
            }
        };
        app.on('frameend', onEnd);
        app.renderNextFrame = true;
    });
}

/** True when a frame came back empty (a canvas read outside its frame). */
function looksBlank(frame: VideoFrame): boolean {
    const c = document.createElement('canvas');
    c.width = 32;
    c.height = 32;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    if (!ctx) return false;
    ctx.drawImage(frame, 0, 0, 32, 32);
    const d = ctx.getImageData(0, 0, 32, 32).data;
    let lit = 0;
    for (let i = 0; i < d.length; i += 4) if (d[i] + d[i + 1] + d[i + 2] > 24) lit++;
    return lit < 8;
}

export async function recordFlythrough(
    t: RecordTarget,
    onProgress: (p: RecordProgress) => void,
    signal: AbortSignal
): Promise<{ bytes: number; frames: number; capped: number }> {
    if (typeof VideoEncoder === 'undefined') throw new Error('this browser cannot encode video (no WebCodecs)');
    const stamp = await sceneStamp(t.sceneUrl);
    if (!stamp) throw new Error('scene.json could not be read for the stamp');
    const size = screenSize();
    const o = orientation();
    // Even dimensions: H.264 4:2:0 needs them. The odd last row/column is
    // cropped, invisible at the fade. Some hardware encoders (the MacBook's,
    // 18/10/2026: "Encoding error" on the first real frame at 2378×1556)
    // want multiples of 16 or the software encoder instead, so the first
    // frame is encoded under each option in turn until one works.
    let w = t.canvas.width & ~1;
    let h = t.canvas.height & ~1;
    const attempts: { w: number; h: number; accel: Accel }[] = [
        { w, h, accel: 'no-preference' },
        { w, h, accel: 'prefer-software' },
        { w: t.canvas.width & ~15, h: t.canvas.height & ~15, accel: 'no-preference' },
        { w: t.canvas.width & ~15, h: t.canvas.height & ~15, accel: 'prefer-software' }
    ];

    let samples: Uint8Array[] = [];
    let keyframes: number[] = [];
    let avcC: Uint8Array | null = null;
    let colour: Mp4Colour | null = null;
    let failure = null as Error | null;
    let encoder = null as VideoEncoder | null;
    let config = null as VideoEncoderConfig | null;

    const openEncoder = (cfg: VideoEncoderConfig): VideoEncoder => {
        samples = [];
        keyframes = [];
        avcC = null;
        colour = null;
        failure = null;
        const enc = new VideoEncoder({
            output: (chunk, meta) => {
                const desc = meta?.decoderConfig?.description;
                if (desc && !avcC) {
                    avcC = ArrayBuffer.isView(desc)
                        ? new Uint8Array(desc.buffer, desc.byteOffset, desc.byteLength).slice()
                        : new Uint8Array(desc).slice();
                    colour = colourFrom(meta?.decoderConfig?.colorSpace);
                }
                const b = new Uint8Array(chunk.byteLength);
                chunk.copyTo(b);
                if (chunk.type === 'key') keyframes.push(samples.length);
                samples.push(b);
            },
            error: (e) => { failure = e as Error; console.error('flythrough encoder error', e); }
        });
        enc.configure(cfg);
        return enc;
    };

    /** Encode the first frame under each option until one survives a flush.
     *  Keeps that encoder (frame 0 is already in it); throws with every
     *  reason when none does. */
    const chooseEncoder = async (frame0: VideoFrame): Promise<void> => {
        const reasons: string[] = [];
        for (const a of attempts) {
            const label = `${a.w}×${a.h} ${a.accel}`;
            let probe: VideoFrame | null = null;
            try {
                const cfg = await pickConfig(a.w, a.h, a.accel);
                encoder = openEncoder(cfg);
                probe = new VideoFrame(frame0, { visibleRect: { x: 0, y: 0, width: a.w, height: a.h } });
                encoder.encode(probe, { keyFrame: true });
                await encoder.flush();
                if (failure || !samples.length) throw failure ?? new Error('no output');
                config = cfg;
                w = a.w;
                h = a.h;
                console.info('flythrough encoder chosen', label, cfg.codec);
                return;
            } catch (e) {
                reasons.push(`${label}: ${(failure as Error | null)?.message ?? (e as Error).message}`);
                if (encoder && encoder.state !== 'closed') encoder.close();
                encoder = null;
            } finally {
                probe?.close();
            }
        }
        throw new Error(`no encoder worked on this device (${reasons.join('; ')})`);
    };

    const frames = Math.max(2, Math.ceil(t.duration * RECORD_FPS) + 1);
    const started = performance.now();
    let capped = 0;
    try {
        for (let i = 0; i < frames; i++) {
            if (signal.aborted) throw new DOMException('recording cancelled', 'AbortError');
            if (failure) throw failure;
            t.applyAt(Math.min(t.duration, i / RECORD_FPS));
            // The first view after a jump needs the most (new tiers in view);
            // the next ones are a sixtieth of a second further along.
            const r = await waitViewSettled(t.app, { capMs: i === 0 ? 15000 : 4000 });
            if (r === 'cap') capped++;
            const frame = await grab(t.app, t.canvas, w, h, Math.round((i * 1e6) / RECORD_FPS));
            try {
                if (i === 0 && looksBlank(frame)) {
                    throw new Error('the canvas reads back empty on this device; recording is not possible here');
                }
                if (i === 0) {
                    await chooseEncoder(frame);   // frame 0 is encoded in the process
                } else {
                    try {
                        encoder!.encode(frame, { keyFrame: i % RECORD_FPS === 0 });
                    } catch (e) {
                        // A closed encoder means it failed on its own; its error
                        // arrives a moment later and is the one worth reporting.
                        await new Promise(res => setTimeout(res, 100));
                        const why = failure as Error | null;
                        throw new Error(`encoder stopped at frame ${i} of ${frames} (${w}×${h}, ${config!.codec}): ${why ? why.message : (e as Error).message}`);
                    }
                }
            } finally {
                frame.close();
            }
            while (encoder!.encodeQueueSize > 4) await new Promise(res => setTimeout(res, 5));
            onProgress({ frame: i + 1, frames, capped, elapsedMs: performance.now() - started });
        }
        await encoder!.flush();
        if (failure) throw failure;
    } finally {
        if (encoder && encoder.state !== 'closed') encoder.close();
    }
    if (!avcC) throw new Error('the encoder gave no H.264 description');
    const blob = writeMp4({ width: w, height: h, fps: RECORD_FPS, avcC, colour, samples, keyframes });
    await storeFlythrough(t.sceneUrl, o, size, stamp, blob);
    return { bytes: blob.size, frames, capped };
}
